// ======================================
// Design Ideas / Inspiration Page Module
// ======================================

import { icons } from '../components.js';
import { designIdeasAPI } from '../lib/api.js';

const categories = [
  { name: 'All', slug: 'all' },
  { name: 'Living Room', slug: 'living-room' },
  { name: 'Bedroom', slug: 'bedroom' },
  { name: 'Kitchen', slug: 'kitchen' },
  { name: 'Bathroom', slug: 'bathroom' },
  { name: 'Dining', slug: 'dining' },
  { name: 'Kids Room', slug: 'kids-room' },
  { name: 'Pooja Room', slug: 'pooja-room' }
];

export function renderDesignIdeasPage(params = {}) {
  const activeCategory = params.category || 'all';

  return `
    <section class="page-header" style="background: linear-gradient(135deg, var(--color-primary), var(--color-secondary)); color: white;">
      <div class="container">
        <div class="page-header-content" style="text-align:center;">
          <p style="color: var(--color-accent-light); font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: var(--space-4);">Inspiration</p>
          <h1 class="page-title" style="color:white;">Design Ideas</h1>
          <p class="page-subtitle" style="color: rgba(255,255,255,0.85); max-width: 640px; margin: 0 auto;">
            Explore stunning interior designs crafted by our experts. Find inspiration for every room in your home.
          </p>
        </div>
      </div>
    </section>

    <!-- Category Filter -->
    <section style="padding: var(--space-10) 0 var(--space-6); border-bottom: 1px solid var(--color-border-light);">
      <div class="container">
        <div style="display: flex; gap: var(--space-3); overflow-x: auto; padding-bottom: var(--space-2); -webkit-overflow-scrolling: touch;" id="design-filters">
          ${categories.map((cat, i) => `
            <a href="/design-ideas/${cat.slug === 'all' ? '' : cat.slug}" 
               data-filter="${cat.slug}" 
               class="btn ${isActive(cat.slug, activeCategory) ? 'btn-primary' : 'btn-outline'}" 
               style="white-space: nowrap; border-radius: var(--radius-full); padding: var(--space-2) var(--space-5); font-size: var(--text-sm); text-decoration: none;">
              ${cat.name}
            </a>
          `).join('')}
        </div>
      </div>
    </section>

    <!-- Design Grid -->
    <section style="padding: var(--space-12) 0 var(--space-20);">
      <div class="container">
        <div id="design-grid" style="display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: var(--space-6); min-height: 400px;">
          <!-- Loading State -->
          <div class="loading-spinner" style="grid-column: 1/-1; text-align: center; padding: 40px;">Loading design ideas...</div>
        </div>

        <div style="text-align: center; margin-top: var(--space-12);">
          <button class="btn btn-primary btn-lg" data-open-modal="lead-modal">Get This Look — Free Consultation</button>
        </div>
      </div>
    </section>
  `;
}

function isActive(slug, current) {
  if (slug === 'all' && (!current || current === 'all')) return true;
  return slug === current;
}

export function initDesignIdeasPage(params = {}) {
  const activeCategory = params.category && params.category !== 'all' ? params.category : null;
  loadDesignIdeas(activeCategory);
}

async function loadDesignIdeas(category) {
  const grid = document.getElementById('design-grid');
  if (!grid) return;

  try {
    const result = await designIdeasAPI.list(category ? { category } : {});
    const data = result.data || {};
    const items = Array.isArray(data) ? data : (data.items || []);

    if (!items || items.length === 0) {
      grid.innerHTML = '<div style="grid-column: 1/-1; text-align: center; color: var(--color-text-muted);">No design ideas found for this category yet.</div>';
      return;
    }

    grid.innerHTML = items.map(idea => `
            <div class="design-card" style="border-radius: var(--radius-xl); overflow: hidden; background: white; box-shadow: var(--shadow-sm); transition: transform 0.3s ease, box-shadow 0.3s ease; cursor: pointer;" 
                 onmouseover="this.style.transform='translateY(-6px)';this.style.boxShadow='var(--shadow-lg)'" 
                 onmouseout="this.style.transform='';this.style.boxShadow='var(--shadow-sm)'">
              <div style="position: relative; padding-top: 66%; overflow: hidden;">
                <img src="${idea.image_url}" alt="${idea.title}" loading="lazy" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover; transition: transform 0.5s ease;" onmouseover="this.style.transform='scale(1.05)'" onmouseout="this.style.transform=''">
                <div style="position: absolute; top: var(--space-3); left: var(--space-3); display: flex; gap: var(--space-2);">
                  ${idea.style ? `<span style="background: rgba(0,0,0,0.7); color: white; padding: var(--space-1) var(--space-3); border-radius: var(--radius-full); font-size: var(--text-xs); font-weight: 600;">${idea.style}</span>` : ''}
                  ${idea.budget ? `<span style="background: var(--color-accent); color: white; padding: var(--space-1) var(--space-3); border-radius: var(--radius-full); font-size: var(--text-xs); font-weight: 600;">${idea.budget}</span>` : ''}
                </div>
              </div>
              <div style="padding: var(--space-5) var(--space-6);">
                <h3 style="font-family: var(--font-heading); font-size: var(--text-lg); margin-bottom: var(--space-2);">${idea.title}</h3>
                <p style="color: var(--color-text-muted); font-size: var(--text-sm); line-height: 1.6;">${idea.description || ''}</p>
              </div>
            </div>
        `).join('');

  } catch (error) {
    console.error('Failed to load design ideas:', error);
    grid.innerHTML = `<div style="color: red; grid-column: 1/-1; text-align: center;">
        <p>Failed to load design ideas.</p>
        <p style="font-size: 0.8em; color: gray;">${error.message || 'Unknown error'}</p>
        <button onclick="window.location.reload()" class="btn btn-sm btn-outline mt-4">Retry</button>
    </div>`;
  }
}
